// src/pages/MyDecks.jsx
import React, { useState, useEffect, useRef } from "react"; // add useRef
import axios from "axios";
import AddSnapDeck from "./sql/AddSnapDeck.jsx";
import UpdateSnapDeck from "./sql/UpdateSnapDeck.jsx";
import DeleteSnapDeck from "./sql/DeleteSnapDeck.jsx";
import GetSnapRankings from "./sql/GetSnapRankings.jsx";
import SnapDeckGroup from "../components/SnapDeckGroup.jsx";
import DividorDropdown from "../components/DividorDropdown.jsx";
import SnapTest from '../components/SnapTest'; 
import "../App.css";
import InfiniteRunnersDecks from "../components/InfiniteRunnerDecks.jsx"
import { showToast } from "../components/utils/toast";
import { copyToClipboard } from "../components/utils/copyToClipboard";
import { encode, decode } from "marvel-snap-deckstrings";
import ReactDOM from "react-dom";

function MyDecks({ user }) {
  const [decks, setDecks] = useState([]);
  const [cardData, setCardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDeck, setShowAddDeck] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [dividors, setDividors] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardRankings, setCardRankings] = useState([]);
  const [cardSearchInput, setCardSearchInput] = useState("");
  const [selectedCards, setSelectedCards] = useState([]);
  const [allCards, setAllCards] = useState([]);

  // 🔍 search states
  const [deckSearch, setDeckSearch] = useState("");

  // inside MyDecks component:
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function fetchAllCards() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}api/data/snapAllCards`);
        setAllCards(res.data || []);
      } catch (err) {
        console.error("Failed loading full card list:", err);
      }
    }
    if (user) fetchAllCards();
  }, [user]);

  // Fetch dividors
  useEffect(() => {
    if (user) {
      axios
        .get(`${process.env.REACT_APP_API_URL}api/data/snapDividors/${user.id}`)
        .then((res) => setDividors(res.data))
        .catch((err) => console.error("Failed to fetch dividors:", err));
    }
  }, [user]);

  function camelToWords(str) {
    if (!str) return "";
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")     // add space before capital letters
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2") // handle acronyms
      .trim();
  }

 function normalizeToDbFormat(str) {
    const merf = camelToWords(str)
      .replace("-", " ")
      .split(" ")
      .map((w, i) =>
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
      )
      .join("")
      .replace("HawkeyeKateBishop", "KateBishop")
      .replace("JuggernautHorseman", "Juggernaut,HorsemanOfWar")
      .replace("SamWilsonCaptainAmerica", "SamWilson")
      .replace("DoctorDoom2099", "Doom2099")
      .replace("AranA", "Arana")
      .replace("MisterNegative", "MrNegative")
      .replace("OmegaSentinel", "KarimaShapandar")
      .replace("StarlordMasterOfTheSun", "StarLordMasterOfTheSun")
      .replace("ShouLaoTheUndying", "ShouLao")
      .replace("VivVision", "Viv")
      .replace("MisterFantastic", "MrFantastic")
      .replace("MODOK", "Modok")
      .replace("BatrocTheLeaper", "Batroc")
      .replace("RonanTheAccuser", "Ronan")
      .replace("TheHood", "Hood")
      .replace("Mbaku", "MBaku")
      .replace("TheLivingTribunal", "LivingTribunal")
      .replace("DoctorDoom", "DrDoom")
      .replace("PhoenixForce", "ThePhoenixForce")
      .replace("GorrTheGodButcher", "Gorr")
      .replace("TheInfinaut", "Infinaut")
      .replace("StarLord", "Starlord")
      .replace("MisterSinister", "MrSinister")
      .replace("TheFallenOne", "FallenOne")
      .replace("JaneFosterMightyThor", "JaneFoster")
      .replace("JoaquinTorresFalconIi", "JoaquinTorres")
      .replace("MisterFantasticFirstSteps", "MrFantasticFirstSteps")
      .replace("Modok", "M.o.d.o.k.")
      .replace("UatuTheWatcher", "Uatu");
    return merf;
  }

  async function copyDeckCode(deck) {
    if (!deck) return;

    // collect all card IDs from the deck row
    const allIds = [];
    for (let i = 1; i <= 12; i++) {
      const cardId = deck[`card${i}`];
      if (cardId) allIds.push(Number(cardId));
    }

    if (allIds.length !== 12) {
      console.warn("Deck must contain 12 cards to copy:", deck);
      return;
    }

    // use already-fetched cardData (no axios here)
    const names = allIds
      .map((id) => cardData?.[id]?.cardname)
      .filter(Boolean)
      .map(normalizeToDbFormat);

    if (names.length !== 12) {
      console.warn("Missing card data for some cards; refresh and try again.");
      return;
    }

    const deckstring = encode({ cards: names });
    const ok = await copyToClipboard(deckstring);

    if (ok) {
      showToast("Deck copied!");
    } else {
      showToast("Copy failed", "error");
    }

    return deckstring;
  }

  // Reorder decks when page opens
  useEffect(() => {
    if (!user) return;
    const reorderDecks = async () => {
      try {
        await axios.put(`${process.env.REACT_APP_API_URL}api/reorderSnapDecks`);
        console.log("Decks reordered successfully");
      } catch (err) {
        console.error("Failed to reorder decks:", err);
      }
    };
    reorderDecks();
  }, [user]);

  function coerceToDeckArray(json) {
    // if file contains "ServerState.Decks"
    if (json?.ServerState?.Decks) {
      return json.ServerState.Decks.map((d) => ({
        deckName: d.Name,
        cards: (d.Cards || []).map((c) => c.CardDefId),
        rank: d.CardBack?.CardBackDefId || null, // optional
      }));
    }

    // fallback: maybe just one deck
    if (json?.Name && json?.Cards) {
      return [
        {
          deckName: json.Name,
          cards: (json.Cards || []).map((c) => c.CardDefId),
        },
      ];
    }

    return [];
  }

  const handleImportFile = async (file) => {
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const decks = coerceToDeckArray(json);

      for (const d of decks) {
        const cards = Array.from({ length: 12 }, (_, i) => d.cards[i] ?? null);

        console.log("Deck JSON:", d);
        console.log("Cards being sent:", cards);

        if (cards.some((c) => !c)) {
          console.warn(`Skipping deck ${d.deckName} — missing cards`);
          continue;
        }

        const payload = {
          deckName: d.deckName,
          userId: user.id,
          versionNum: "-",
          monthCompleted: "-",
          deckRank: d.rank || "Neutral",
          card1: cards[0], card2: cards[1], card3: cards[2], card4: cards[3],
          card5: cards[4], card6: cards[5], card7: cards[6], card8: cards[7],
          card9: cards[8], card10: cards[9], card11: cards[10], card12: cards[11],
        };

        try {
          await axios.post(`${process.env.REACT_APP_API_URL}api/addSnapDeck`, payload);
        } catch (err) {
          if (err.response?.status === 409) {
            console.warn(`Deck already exists: ${d.deckName}`);
          } else {
            throw err;
          }
        }
      }

      await fetchDecks();
      alert(`${decks.length} deck(s) imported (duplicates skipped).`);
    } catch (err) {
      console.error("Failed to import decks:", err);
      alert("Import failed. Check console for details.");
    }
  };

  // Fetch decks & their cards
  const fetchDecks = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // 1. Get all decks for the user
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}`
      );
      const userDecks = res.data || [];
      setDecks(userDecks);

      // 2. Collect all unique card IDs across decks
      const allIds = new Set();
      userDecks.forEach((deck) => {
        for (let i = 1; i <= 12; i++) {
          const cardId = deck[`card${i}`];
          if (cardId) allIds.add(cardId);
        }
      });

      // 3. Fetch all cards in parallel
      const requests = Array.from(allIds).map((id) =>
        axios
          .get(`${process.env.REACT_APP_API_URL}api/data/snapCards/${id}`, {
            params: { userId: user.id }   // 👈 pass logged-in user's id
          })
          .then((res) => [id, res.data])
          .catch((err) => {
            console.warn("Could not fetch card", id, err.response?.status);
            return null;
          })
      );

      const results = await Promise.all(requests);

      // 4. Build a lookup object for card data
      const fetchedCards = {};
      results.forEach((entry) => {
        if (entry) {
          const [id, data] = entry;
          fetchedCards[id] = data;
        }
      });

      setCardData(fetchedCards);
    } catch (err) {
      console.error("Failed to fetch decks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecks();
  }, [user]);

  // 🔍 search handlers
  const handleDeckSearch = async () => {
    if (!deckSearch.trim()) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}/search/deck/${deckSearch}`
      );
      const results = res.data || [];

      // group results back into deck groups
      setDecks(results);
    } catch (err) {
      console.error("Failed deck search:", err);
    }
  };

  const handleMultiCardSearch = async () => {
    if (selectedCards.length === 0) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}/search/cards`,
        { cards: selectedCards }
      );
      setDecks(res.data || []);
    } catch (err) {
      console.error("Failed card multi-search:", err);
    }
  };

  const handleClearSearch = () => {
    setDeckSearch("");
    setSelectedCards([]);
    setCardSearchInput("");
    fetchDecks();
  };

  if (loading) {
    return (
      <>
        <br /><br /><br />
        <p style={{ textAlign: "center", color: "#ccc" }}>
          Loading decks…
        </p>
      </>
    )
  }

  return (
    <div className="list-container">
      <h1 className="main-title" style={{ textAlign: "center" }}>
        My Decks
      </h1>
      <SnapTest user={user}/>
      <br />
      <div className="snap-toolbar">
  <div style={{ display: "inline-block", position: "relative" }}>
    <DividorDropdown
      dividors={dividors}
      user={user}
      setDecks={setDecks}
      setDividors={setDividors}
    />
  </div>

  <select className="snap-select">
    <option value="">View Tournament Decks</option>
  </select>

  <button
    className="snap-btn snap-btn--primary"
    onClick={async () => {
      const name = window.prompt("Enter a name for the new dividor:");
      if (!name) return;

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}api/addSnapDividor`,
          { dividor: name, userId: user.id }
        );
        setDividors((prev) => [...prev, res.data.dividor]);
      } catch (err) {
        console.error("Failed to add dividor:", err);
        alert("Error adding dividor. Check console for details.");
      }
    }}
  >
    Add Dividor
  </button>

  {/* <button
    className="snap-btn snap-btn--blue"
    onClick={() => (window.location.href = "/deck-positions")}
  >
    View Deck Positions
  </button> */}

  <button className="snap-btn" onClick={() => setShowAddDeck(true)}>
    Add Deck
  </button>

  <input
    type="file"
    accept=".json,application/json"
    style={{ display: "none" }}
    ref={fileInputRef}
    onChange={(e) => {
      const file = e.target.files?.[0];
      if (file) handleImportFile(file);
      e.target.value = "";
    }}
  />
</div>

      {/* 🔍 Search Bars */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search by Deck Name..."
          value={deckSearch}
          onChange={(e) => setDeckSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleDeckSearch()}
          style={{ marginRight: "8px", padding: "6px", borderRadius: "6px" }}
        />
        <button className="button" onClick={handleDeckSearch}>
          Search Decks
        </button>

        {/* MULTI-CARD SEARCH UI */}
        <div style={{ marginTop: "10px" }}>

          {/* Selected Chips */}
          <div style={{ marginBottom: "8px" }}>
            {selectedCards.map((c) => (
              <span
                key={c}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  background: "#333",
                  borderRadius: "14px",
                  marginRight: "6px",
                  color: "white",
                  cursor: "default",
                }}
              >
                {c}
                <span
                  style={{ marginLeft: "6px", cursor: "pointer", color: "#f55" }}
                  onClick={() =>
                    setSelectedCards((prev) => prev.filter((x) => x !== c))
                  }
                >
                  ×
                </span>
              </span>
            ))}
          </div>

          {/* Searchable Dropdown Input */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <input
              type="text"
              placeholder="Search card name..."
              value={cardSearchInput}
              onChange={(e) => setCardSearchInput(e.target.value)}
              style={{ padding: "6px", borderRadius: "6px", width: "200px" }}
            />

            {/* Dropdown */}
            {cardSearchInput.trim() !== "" && (
              <div
                style={{
                  position: "absolute",
                  top: "34px",
                  left: 0,
                  width: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  background: "#222",
                  border: "1px solid #444",
                  borderRadius: "6px",
                  zIndex: 50,
                }}
              >
                {allCards
                  .filter((c) =>
                    c.cardname.toLowerCase().includes(cardSearchInput.toLowerCase())
                  )
                  .slice(0, 25)
                  .map((c) => (
                    <div
                      key={c.id}
                      style={{
                        padding: "6px 8px",
                        cursor: "pointer",
                        color: "white",
                      }}
                      onClick={() => {
                        setSelectedCards((prev) =>
                          prev.includes(c.cardname) ? prev : [...prev, c.cardname]
                        );
                        setCardSearchInput(""); // clear search box
                      }}
                    >
                      {c.cardname}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <button
            className="button"
            style={{ marginLeft: "8px" }}
            onClick={handleMultiCardSearch}
          >
            Search Cards
          </button>
        </div>
        <button
          className="button"
          style={{ marginLeft: "16px", background: "#444" }}
          onClick={handleClearSearch}
        >
          Clear
        </button>
      </div>

      {decks.length === 0 ? (
        <p style={{ textAlign: "center", color: "#aaa" }}>
          You don't have any decks yet.
        </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: "24px",
                  gridTemplateColumns: "1fr",
                  marginTop: "20px",
                  justifyContent: "center",
                }}
              >
                {dividors.map((div) => {
                  const groupDecks = decks.filter((d) => d.deckrank === div.dividor);

                  // console.log("Grouping dividor:", div.dividor, "→ Matched decks:", groupDecks);

                  return (
                    <SnapDeckGroup
                      key={div.id}
                      dividor={div}
                      decks={groupDecks}
                      cardData={cardData}
                      setSelectedDeck={setSelectedDeck}
                      setSelectedCard={setSelectedCard}
                      fetchDecks={fetchDecks}
                      copyDeckCode={copyDeckCode}
                    />
                  );
                })}
              </div>
      )}

      {showAddDeck && (
        <AddSnapDeck
          onClose={() => setShowAddDeck(false)}
          setDecks={setDecks}
          user={user}
          dividors={dividors}
        />
      )}
      {selectedDeck && (
        <UpdateSnapDeck
          deck={selectedDeck}
          onClose={() => setSelectedDeck(null)}
          setDecks={setDecks}
        />
      )}

      {/* === Info Modal === */}
      {selectedCard &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay"
            onClick={() => setSelectedCard(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10001,
              background: "rgba(0,0,0,0.78)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "24px",
              boxSizing: "border-box",
            }}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "720px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
                zIndex: 10002,
              }}
            >
              <h2
                className="modal-title"
                style={{
                  textAlign: "center",
                  marginBottom: "16px",
                  color: "rgb(197,176,55)",
                }}
              >
                {selectedCard.cardname}
              </h2>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <img
                  src={
                    selectedCard.customhtml && selectedCard.customhtml.trim() !== ""
                      ? selectedCard.customhtml
                      : selectedCard.cardhtml
                  }
                  alt={selectedCard.cardname}
                  className="modal-card-image"
                  style={{
                    maxWidth: "300px",
                    borderRadius: "10px",
                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                  }}
                />
              </div>

              <p
                style={{
                  color: "#ddd",
                  textAlign: "center",
                  fontSize: "1rem",
                  marginBottom: "16px",
                }}
              >
                {selectedCard.carddesc}
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(100px, 1fr))",
                  gap: "14px",
                  marginBottom: "16px",
                }}
              >
                <div className="rank-value" style={{ textAlign: "center" }}>
                  <strong>Reliance</strong>
                  <div>{selectedCard.reliancescore ?? 0}</div>
                </div>

                <div className="rank-value" style={{ textAlign: "center" }}>
                  <strong>Variants</strong>
                  <div>{selectedCard.variantsscore ?? 0}</div>
                </div>

                <div className="rank-value" style={{ textAlign: "center" }}>
                  <strong>Usability</strong>
                  <div>{selectedCard.usabilityscore ?? 0}</div>
                </div>

                <div
                  className="rank-value"
                  style={{
                    textAlign: "center",
                    background: "rgba(197,176,55,0.15)",
                    borderRadius: "8px",
                  }}
                >
                  <strong>Average</strong>
                  <div>
                    {(
                      ((selectedCard.reliancescore ?? 0) +
                        (selectedCard.variantsscore ?? 0) +
                        (selectedCard.usabilityscore ?? 0)) /
                      3
                    ).toFixed(2)}
                  </div>
                </div>
              </div>

              <InfiniteRunnersDecks
                userId={user?.id}
                cardName={selectedCard.cardname}
              />

              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <button className="button" onClick={() => setSelectedCard(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
      )}
      {selectedCard && (
        <GetSnapRankings
          setData={setCardRankings}
          route={`${process.env.REACT_APP_API_URL}api/data/snapRankings/card/${selectedCard.id}`}
        />
      )}
    </div>
  );
}

export default MyDecks;
